import * as React from 'react';
import { useMemo } from 'react';
import { Link, RouteComponentProps, useHistory, withRouter } from 'react-router-dom';

import { useFieldDescription } from '../../../shared/api/apiHooks';
import { FormattedMessageHelper } from '../../../shared/helpers/internationalization';
import { getDatasetsPath, getMainDataPath, getSingleDatasetPath } from '../dataExplorerRouterPaths';
import { Breadcrumbs } from '../../../components/Breadcrumbs/Breadcrumbs';
import {
  CommonDataFieldsSelector,
  commonFieldTransformers,
  getDefaultCommonDataFilterValues,
} from '../CommonDataFieldsSelector/CommonDataFieldsSelector';
import { useFilterForm } from '../Filters/useFilterForm';
import Skeleton from 'react-loading-skeleton';
import { addModifiers } from '../../../shared/helpers';
import { useDataExplorerTrackCustomEvent } from '../Filters/dataExplorerAnalytics';
import CloneAlpha from '../../../components/Learn/LearnBlocks/CloneAlpha';
import Immutable from 'immutable';
import { DataFieldRegionsTable } from '../RegionTables/DataFieldRegionsTable';
import { DataExplorerLink } from '../dataExplorerHistoryContext';
import { DataFieldType, FieldDescriptionResponse, postFieldVisualization, Theme } from '../../../shared/api/api';
import { CopyToClipboard } from '../CopyToClipboard/CopyToClipboard';
import { formatDataFieldType } from '../Filters/FilterFields/dataExplorerFieldsConfig';
import '../Data.scss';
import './DataField.scss';
import { AlphaStatusThemeLabel } from '../../../components/HOCStageChooser/HOCStageChooser';
import { useIntl } from 'react-intl';
import { Button, Modal, Popup } from 'semantic-ui-react';
import { useSelector } from 'react-redux';
import { getUserPermissionSet } from '../../../reducers/AuthenticationReducer/AuthenticationSelector';
import { BRAIN_LABS_URL } from '../../../shared/constants';

interface DatasetRouteProps {
  datasetId: string;
  fieldId: string;
}

interface CategoryParams {
  category?: { id: string; name: string };
  subcategory?: { id: string; name: string };
}

export function CategoryLinks({ category, subcategory }: CategoryParams) {
  return (
    <p className="data-field__type">
      <span>
        <FormattedMessageHelper id="dataExplorer.dataset.category" />:
        <span className="data-field__type-value">
          {' '}
          {category?.name && subcategory ? (
            <>
              <Link className="link" to={getDatasetsPath({ queryParams: { category: category.id } })}>
                {category.name}
              </Link>
              {' / '}
              <Link className="link" to={getDatasetsPath({ queryParams: { subcategory: subcategory.id } })}>
                {subcategory.name}
              </Link>
            </>
          ) : (
            <Skeleton count={1} width={200}></Skeleton>
          )}
        </span>
      </span>
    </p>
  );
}

interface BrainLabsButtonProps {
  onClick: () => Promise<void>;
  fieldDescription?: FieldDescriptionResponse;
  labsLoading: boolean;
  id: string;
}

function BrainLabsButton({ onClick, labsLoading, id, fieldDescription }: BrainLabsButtonProps) {
  return (
    <Button
      onClick={onClick}
      className="button--primary button--md data-field__actions-labs"
      disabled={!fieldDescription?.visualizable || labsLoading}
    >
      <i className="data-field__actions-labs-icon" />
      <FormattedMessageHelper tagName="span" id={id} />
    </Button>
  );
}

function DataField({ match }: RouteComponentProps<DatasetRouteProps>) {
  const fieldId = match.params.fieldId;
  const intl = useIntl();
  const history = useHistory();

  const defaultValues = useMemo(() => ({ ...getDefaultCommonDataFilterValues() }), []);
  const { control, values } = useFilterForm({
    fieldTransformers: commonFieldTransformers,
    defaultValues,
    debounce: 100,
  });

  const fieldDescriptionsResult = useFieldDescription({ id: fieldId });
  const { isLoading: isFieldDescriptionLoading, data: fieldDescription } = fieldDescriptionsResult;
  const permissions = useSelector(getUserPermissionSet);
  const isBrainLabsEnabled = permissions?.has('BRAIN_LABS');

  const { region, delay, universe } = values;
  const trackEvent = useDataExplorerTrackCustomEvent();

  const alphaObject = useMemo(
    () => getAlphaObject({ code: fieldId, delay, region, universe, type: fieldDescription?.type }),
    [fieldId, region, delay, universe, fieldDescription?.type]
  );

  const currentUniverseThemes = useMemo(
    () =>
      fieldDescription?.data.find(field => field.delay.toString() === delay && field.region === region && field.universe === universe)
        ?.themes ?? [],
    [delay, region, universe, fieldDescription]
  );

  const [isLabsModalOpen, setIsLabsModalOpen] = React.useState(false);
  const handleCloseLabsModal = () => setIsLabsModalOpen(false);

  const [isLabsLoading, setIsLabsLoading] = React.useState(false);

  if (!fieldId) {
    return <div>No field id provided</div>;
  }

  const handleVisualize = async () => {
    if (isLabsLoading) {
      return;
    }
    setIsLabsLoading(true);
    setIsLabsModalOpen(true);
    await postFieldVisualization({ id: fieldId, region, delay, universe });
    setIsLabsLoading(false);
  };

  const handleOpenLabsFromModal = () => {
    history.push(BRAIN_LABS_URL);
  };

  const labsButtonLabel = isLabsLoading ? 'dataExplorer.field.labsLoading' : 'dataExplorer.field.openBrainLabs';
  const fieldOpeningMessage = 'dataExplorer.field.labsModalText';

  const datasetId = fieldDescription?.dataset.id;
  return (
    <div className="data-main">
      <div className={addModifiers('data__header-container', ['sticky'])}>
        <div className="data-breadcrumbs">
          <Breadcrumbs
            items={[
              {
                link: getMainDataPath(),
                LinkComponent: DataExplorerLink,
                name: <FormattedMessageHelper id="dataExplorer.title" />,
                onClickCapture: () => trackEvent('de_click_breadcrumb_main'),
              },
              {
                name: <FormattedMessageHelper id="dataExplorer.breadcrumbs.browseDatasets" />,
                LinkComponent: DataExplorerLink,
                link: getDatasetsPath(),
                onClickCapture: () => trackEvent('de_click_breadcrumb_datasets_list'),
              },
              {
                name: fieldDescription?.dataset?.name,
                link: datasetId && getSingleDatasetPath({ datasetId }),
                LinkComponent: DataExplorerLink,
                onClickCapture: () => trackEvent('de_click_breadcrumb_dataset'),
              },
              {
                name: (
                  <span>
                    {fieldId} <CopyToClipboard className={'copy-to-clipboard--field-page'} value={fieldId} />
                  </span>
                ),
              },
            ]}
          />
        </div>
        <CommonDataFieldsSelector control={control} />
      </div>
      <div className="data-field__actions-container">
        <CloneAlpha
          alpha={alphaObject}
          buttonLabelKey="dataExplorer.field.simulate"
          containerClass="data-page__simulate-container"
          isDisabled={isFieldDescriptionLoading}
        />
        {isBrainLabsEnabled && (
          <Popup
            content={<FormattedMessageHelper id="dataExplorer.field.disabledLabsHint" />}
            position="bottom center"
            on={'hover'}
            disabled={fieldDescription?.visualizable || isLabsLoading || isFieldDescriptionLoading}
            trigger={
              <div>
                <BrainLabsButton
                  onClick={handleVisualize}
                  fieldDescription={fieldDescription}
                  labsLoading={isLabsLoading}
                  id={labsButtonLabel}
                />
              </div>
            }
          />
        )}
      </div>
      <h2>
        <FormattedMessageHelper id="dataExplorer.datasetField.description" />
      </h2>
      <div className="data-single-entity__description">
        {isFieldDescriptionLoading ? (
          <div>
            <Skeleton count={1} width={300}></Skeleton>
            <Skeleton containerClassName="data-field__skeleton--type" count={1}></Skeleton>
            <br />
            <Skeleton containerClassName="data-field__skeleton--description" count={1} />
          </div>
        ) : (
          <div>
            <CategoryLinks category={fieldDescription?.category} subcategory={fieldDescription?.subcategory} />
            <p className="data-field__type">Type: {formatDataFieldType(fieldDescription?.type)}</p>
            <br />
            <p>{fieldDescription?.description}</p>
          </div>
        )}
      </div>
      <div className="data-single-entity__themes">
        {currentUniverseThemes &&
          currentUniverseThemes?.length > 0 &&
          (currentUniverseThemes as unknown as Theme[])?.map(theme => <AlphaStatusThemeLabel key={theme.id} theme={theme} intl={intl} />)}
      </div>
      <DataFieldRegionsTable fieldDescriptionResult={fieldDescriptionsResult} control={control} />
      {isBrainLabsEnabled && (
        <div className="data-field__lab-graph">
          <div className="data-field__lab_graph-button-container">
            <FormattedMessageHelper id={'dataExplorer.field.labsDescription'} tagName={'p'} />
            <BrainLabsButton
              onClick={handleVisualize}
              fieldDescription={fieldDescription}
              labsLoading={isLabsLoading}
              id={labsButtonLabel}
            />
          </div>
        </div>
      )}
      <Modal open={isLabsModalOpen} size="tiny" closeIcon={true} onClose={handleCloseLabsModal}>
        <Modal.Header>
          <FormattedMessageHelper id="dataExplorer.field.labsModalTitle" />
        </Modal.Header>
        <Modal.Content>
          <div className="modal__content-text labs-modal__content-text">
            <FormattedMessageHelper id={fieldOpeningMessage} values={{ fieldId }} />

            <p className="labs-modal__content-explanation">
              <FormattedMessageHelper id="dataExplorer.field.labsModalHint" tagName="h3" />
              <FormattedMessageHelper id="dataExplorer.field.labsModalExplanation" values={{ fieldId }} />
            </p>
          </div>
          <div className="modal__section--buttons modal__section--buttons--right">
            <button onClick={handleCloseLabsModal} className="button button--secondary">
              <FormattedMessageHelper id="dataExplorer.field.closeModal" />
            </button>
            <button onClick={handleOpenLabsFromModal} className="button button--primary data-field__actions-labs">
              <i className="data-field__actions-labs-icon" />
              <FormattedMessageHelper id="brainLabs.login" />
            </button>
          </div>
        </Modal.Content>
      </Modal>
    </div>
  );
}

interface AlphaCodeProperties {
  code: string;
  region: string;
  delay: number;
  universe: string;
  type?: DataFieldType;
}

function getAlphaObject({ code, region, delay, universe, type }: AlphaCodeProperties) {
  const augmentGroupCode = (code: string) => `
//alpha_expr can be replaced by any other idea

alpha_expr = -returns;

group_neutralize(alpha_expr, densify(${code}))`;

  const augmentVectorCode = (code: string) => `
//vec_avg() can be replaced by any other vector operator
  
vec_avg(${code})`;

  let augmentedCode = code;

  if (type === 'GROUP') {
    augmentedCode = augmentGroupCode(code);
  } else if (type === 'VECTOR') {
    augmentedCode = augmentVectorCode(code);
  }

  return Immutable.fromJS({
    settings: {
      nanHandling: 'ON',
      instrumentType: 'EQUITY',
      delay: delay,
      visualization: false,
      universe: universe,
      truncation: 0.08,
      unitHandling: 'VERIFY',
      pasteurization: 'ON',
      region: region,
      language: 'FASTEXPR',
      decay: 4,
      neutralization: 'SECTOR',
    },
    color: null,
    os: null,
    is: null,
    hidden: false,
    themes: null,
    author: null,
    name: null,
    dateSubmitted: null,
    performanceAlphaLastDate: null,
    status: 'UNSUBMITTED',
    competitions: null,
    performanceAlphaFetching: false,
    dateModified: '2023-04-27T05:09:50-04:00',
    prod: null,
    grade: null,
    performanceAlphaSummary: [],
    tags: [],
    type: 'REGULAR',
    dateCreated: '2023-04-27T01:09:50-04:00',
    id: '56pnqKN',
    favorite: false,
    regular: {
      code: augmentedCode,
      description: null,
      operatorCount: 0,
    },
    team: null,
    category: null,
    stage: 'IS',
  });
}

export default withRouter(DataField);
